import type { Dispatch, PropsWithChildren, SetStateAction } from "react";
import { createContext, useContext, useEffect, useId, useRef, useState } from "react";
import clsx from "clsx";

type FormControlContextType = {
  controlId?: string;
  controlError: boolean;
  setControlError: Dispatch<SetStateAction<boolean>>;
};

export const FormControlContext = createContext<FormControlContextType>({
  controlId: "",
  controlError: false,
  setControlError: () => {},
});

const FormControlProvider = ({ children }: PropsWithChildren) => {
  const [controlError, setControlError] = useState(false);

  const state = useRef({
    controlId: useId(),
  }).current;

  return <FormControlContext.Provider value={{ controlError, setControlError, ...state }}>{children}</FormControlContext.Provider>;
};

export const FormControl = ({ children }: PropsWithChildren) => {
  return (
    <FormControlProvider>
      <div>{children}</div>
    </FormControlProvider>
  );
};

const Label = ({ children }: PropsWithChildren) => {
  const { controlId, controlError } = useContext(FormControlContext);

  return (
    <label htmlFor={controlId} className={clsx("mb-1.5 ml-4 block text-sm font-medium", controlError ? "text-danger" : "text-light")}>
      {children}
    </label>
  );
};

const Error = ({ children }: PropsWithChildren) => {
  const { setControlError } = useContext(FormControlContext);

  useEffect(() => setControlError(!!children), [children, setControlError]);

  return children ? <div className="ml-4 mt-1.5 text-xs text-danger">{children}</div> : null;
};

const ErrorAbsolute = ({ children }: PropsWithChildren) => {
  const { setControlError } = useContext(FormControlContext);

  useEffect(() => setControlError(!!children), [children, setControlError]);

  return children ? <div className="absolute ml-4 mt-0.5 text-xs text-danger">{children}</div> : null;
};

FormControl.Label = Label;
FormControl.Error = Error;
FormControl.ErrorAbsolute = ErrorAbsolute;
